import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Mic, Monitor, RefreshCw, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PitchScreenShareStage } from '@/components/pitch/PitchScreenShareStage';
import { TeleprompterPanel } from '@/components/pitch/TeleprompterPanel';
import { usePitchRecorder } from '@/lib/pitch/usePitchRecorder';
import { backgroundTemplateImageUrl } from '@/lib/videoEditorBackgroundTemplates';
import { toast } from 'sonner';

/**
 * Records one module. The screen recorder is owned by the page so a share survives
 * switching modules; start/stop are exposed through controlsRef for the floating toolbar.
 */
export function PitchRecordStudio({
  segment,
  recordingMode = 'teleprompter',
  capture = 'audio',
  speakerCameraEnabled = false,
  systemAudioEnabled = false,
  screen,
  backgroundTemplateId = '',
  controlsRef,
  onBeforeStart,
  onRecordingChange,
  onRecorded,
  onReRecord,
  onTeleprompterChange,
}) {
  const screenShareEnabled = capture === 'screen';
  const cameraEnabled = capture === 'camera';
  const backgroundUrl = cameraEnabled && backgroundTemplateId
    ? backgroundTemplateImageUrl(backgroundTemplateId)
    : '';
  const camera = usePitchRecorder({
    withCamera: cameraEnabled,
    backgroundImageUrl: backgroundUrl,
  });
  const recording = screenShareEnabled ? screen.recording : camera.recording;
  const error = screenShareEnabled ? screen.error : camera.error;
  const start = screenShareEnabled ? screen.start : camera.start;
  const stop = screenShareEnabled ? screen.stop : camera.stop;
  const previewStream = cameraEnabled ? camera.previewStream : null;
  const previewLoading = cameraEnabled ? camera.previewLoading : false;
  const [previewUrl, setPreviewUrl] = useState(null);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const [localTake, setLocalTake] = useState(null);
  const [teleprompterText, setTeleprompterText] = useState(segment?.teleprompter_text || '');
  const videoPreviewRef = useRef(null);
  const recordingRef = useRef(false);
  const stopInFlightRef = useRef(false);
  const startInFlightRef = useRef(false);
  const lastShareLostRef = useRef(screen.shareLostSeq);
  const handleStopRef = useRef(null);
  const segmentTextRef = useRef(segment?.teleprompter_text || '');
  recordingRef.current = recording;
  segmentTextRef.current = segment?.teleprompter_text || '';

  const savedRecording = segment?.recording;
  const hasSavedTake = Boolean(savedRecording?.audio_url);
  const showSavedTake = hasSavedTake && !localTake && !recording;
  const needsShare = screenShareEnabled && !screen.sharing;

  useEffect(() => {
    const el = videoPreviewRef.current;
    if (!el) return;
    if (previewStream) {
      el.srcObject = previewStream;
      el.play().catch(() => {});
      return;
    }
    el.srcObject = null;
  }, [previewStream]);

  useEffect(() => {
    setPreviewUrl(null);
    setRecordedVideoUrl(null);
    setLocalTake(null);
    setTeleprompterText(segmentTextRef.current);
  }, [segment?.id]);

  useEffect(() => {
    onRecordingChange?.(recording);
  }, [recording, onRecordingChange]);

  useEffect(() => () => {
    onRecordingChange?.(false);
  }, [onRecordingChange]);

  const handleShare = useCallback(async () => {
    try {
      await screen.shareScreen();
      toast.message('Screen shared — press Start recording');
    } catch (err) {
      toast.error(err?.message || 'Could not share the screen');
    }
  }, [screen]);

  const handleStart = useCallback(async () => {
    if (recordingRef.current || startInFlightRef.current) return;
    // Share picker needs this click's user activation — share first, record on the next click.
    if (needsShare) {
      await handleShare();
      return;
    }
    startInFlightRef.current = true;
    setPreviewUrl(null);
    setRecordedVideoUrl(null);
    setLocalTake(null);
    try {
      // Start capture first so mic/display permission keeps this click's user gesture.
      // Opening Document PiP first steals activation and makes Start appear broken.
      await start();
      try {
        onBeforeStart?.();
      } catch (pipErr) {
        console.warn('Floating recorder window unavailable:', pipErr);
      }
    } catch (err) {
      toast.error(err?.message || 'Could not start recording');
    } finally {
      startInFlightRef.current = false;
    }
  }, [handleShare, needsShare, onBeforeStart, start]);

  const handleStop = useCallback(async () => {
    if (stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    try {
      const result = await stop();
      if (!result?.audioBlob || result.audioBlob.size < 64) {
        toast.error('No audio captured — check microphone permissions');
        return;
      }
      const url = URL.createObjectURL(result.audioBlob);
      setPreviewUrl(url);
      setLocalTake(result);
      if (result.videoBlob?.size) {
        setRecordedVideoUrl(URL.createObjectURL(result.videoBlob));
      }
      onRecorded?.(result);
    } catch (err) {
      if (err?.message === 'Not recording') return;
      toast.error(err?.message || 'Could not save recording');
    } finally {
      stopInFlightRef.current = false;
    }
  }, [onRecorded, stop]);

  handleStopRef.current = handleStop;

  useEffect(() => {
    if (!controlsRef) return undefined;
    controlsRef.current = {
      start: handleStart,
      stop: handleStop,
      needsShare,
    };
    return () => {
      if (controlsRef.current?.start === handleStart) {
        controlsRef.current = null;
      }
    };
  }, [controlsRef, handleStart, handleStop, needsShare]);

  useEffect(() => {
    if (!screenShareEnabled || !screen.shareLostSeq) return;
    if (screen.shareLostSeq === lastShareLostRef.current) return;
    lastShareLostRef.current = screen.shareLostSeq;
    if (!recordingRef.current) return;
    handleStopRef.current?.();
  }, [screen.shareLostSeq, screenShareEnabled]);

  const showTeleprompter = recordingMode === 'teleprompter';
  const waitingForCutout = Boolean(backgroundUrl) && (previewLoading || !previewStream);

  let startLabel = hasSavedTake && !localTake ? 'Record new take' : 'Start recording';
  if (needsShare) startLabel = 'Share screen';

  return (
    <div className="space-y-4">
      {showTeleprompter ? (
        <TeleprompterPanel
          text={teleprompterText}
          active={recording}
          editable
          onTextChange={(next) => {
            setTeleprompterText(next);
            onTeleprompterChange?.(segment?.id, next);
          }}
          className="h-48 sm:h-64"
        />
      ) : (
        <div className="rounded-xl border border-dashed border-slate-600 bg-slate-900/50 p-6 text-center text-slate-400 text-sm">
          Independent audio — speak without a teleprompter. Your audio is recorded in one take for this module.
        </div>
      )}

      {screenShareEnabled ? (
        <div className="space-y-2">
          <PitchScreenShareStage
            screenStream={screen.screenStream}
            speakerStream={speakerCameraEnabled ? screen.speakerStream : null}
            speakerEnabled={speakerCameraEnabled}
            speakerLoading={screen.speakerLoading}
            onShare={recording ? undefined : handleShare}
          />
          {speakerCameraEnabled ? (
            <p className="text-xs text-slate-500">
              Speaker camera records in parallel and sits in the floating window along the bottom of the shared screen.
            </p>
          ) : null}
          {systemAudioEnabled ? (
            <p className="text-xs text-slate-500">
              System/tab audio is on — when sharing, pick a Chrome Tab and enable “Share tab audio” so product sound mixes with your mic
              {screen.systemAudioActive ? ' · tab audio connected' : ''}.
            </p>
          ) : null}
        </div>
      ) : null}

      {cameraEnabled ? (
        <div className="relative aspect-video max-h-56 rounded-xl overflow-hidden bg-black border border-slate-700">
          {backgroundUrl && waitingForCutout ? (
            <img
              src={backgroundUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-80"
            />
          ) : null}
          <video
            key={backgroundTemplateId || 'camera-preview'}
            ref={videoPreviewRef}
            autoPlay
            muted
            playsInline
            className="relative z-10 w-full h-full object-cover"
          />
          {waitingForCutout ? (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 text-slate-400 text-xs bg-black/70">
              <Camera className="w-5 h-5" />
              {backgroundUrl ? 'Applying person cutout & background…' : 'Starting camera…'}
            </div>
          ) : null}
          {!recording && !waitingForCutout && !previewStream ? (
            <div className="absolute inset-0 z-20 flex items-center justify-center text-slate-500 text-xs">
              <Camera className="w-5 h-5 mr-2" /> Allow camera access to preview
            </div>
          ) : null}
        </div>
      ) : null}

      {showSavedTake ? (
        <div className="rounded-lg border border-emerald-800/60 bg-emerald-950/20 p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-emerald-300/90 font-medium">Saved take on server</p>
            {onReRecord ? (
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onReRecord}>
                <RefreshCw className="w-3.5 h-3.5" />
                Re-record this module
              </Button>
            ) : null}
          </div>
          <audio controls src={savedRecording.audio_url} className="w-full" />
          {savedRecording.video_url ? (
            <video
              controls
              src={savedRecording.video_url}
              className="w-full max-h-56 rounded-lg border border-slate-700"
            />
          ) : null}
          {savedRecording.duration_sec ? (
            <p className="text-xs text-slate-500">
              Duration: {Math.round(savedRecording.duration_sec)}s
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="text-sm text-red-400">{error}</p> : null}

      <div className="flex flex-wrap gap-2">
        {!recording ? (
          <Button type="button" onClick={handleStart} className="gap-2">
            {screenShareEnabled ? <Monitor className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            {startLabel}
          </Button>
        ) : (
          <Button type="button" variant="destructive" onClick={handleStop} className="gap-2">
            <Square className="w-4 h-4" /> Stop & upload take
          </Button>
        )}
        {screen.sharing && !recording ? (
          <Button type="button" variant="outline" onClick={screen.stopSharing}>
            Stop sharing
          </Button>
        ) : null}
      </div>

      {previewUrl && localTake ? (
        <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
          <p className="text-xs text-slate-400">Latest local preview (uploading to cloud…)</p>
          <audio controls src={previewUrl} className="w-full" />
        </div>
      ) : null}

      {recordedVideoUrl ? (
        <div className="space-y-1">
          <p className="text-xs text-slate-400">Recorded video preview</p>
          <video controls src={recordedVideoUrl} className="w-full max-h-56 rounded-lg border border-slate-700" />
        </div>
      ) : null}
    </div>
  );
}

export default PitchRecordStudio;
